import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PaymentForm } from '../components/PaymentForm';
import { isStripeEnabled } from '../lib/stripe';
import {
  FileText,
  CreditCard,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react';

interface Invoice {
  id: string;
  invoiceNumber: string;
  type: string;
  status: string;
  description: string | null;
  subtotal: number;
  tax: number;
  total: number;
  amountPaid: number;
  balance: number;
  issueDate: string;
  dueDate: string;
  paidDate: string | null;
  lineItems: {
    id: string;
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }[];
  payments: {
    id: string;
    amount: number;
    method: string;
    processedAt: string;
  }[];
}

interface PaymentIntent {
  clientSecret: string;
  paymentIntentId: string;
}

interface StripeConfig {
  stripeConfigured: boolean;
}

export function BillingPage() {
  const queryClient = useQueryClient();
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);
  const [payingInvoice, setPayingInvoice] = useState<Invoice | null>(null);
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const { data: stripeConfig } = useQuery({
    queryKey: ['stripe-config'],
    queryFn: async () => {
      const response = await api.get<StripeConfig>('/payments/config');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['my-invoices'],
    queryFn: async () => {
      const response = await api.get<Invoice[]>('/invoices/my');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const createPaymentIntentMutation = useMutation({
    mutationFn: async ({ invoiceId, amount }: { invoiceId: string; amount: number }) => {
      const response = await api.post<PaymentIntent>('/payments/create-invoice-intent', {
        invoiceId,
        amount,
      });
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
  });

  const handlePayInvoice = async (invoice: Invoice) => {
    try {
      const intent = await createPaymentIntentMutation.mutateAsync({
        invoiceId: invoice.id,
        amount: invoice.balance,
      });
      setPayingInvoice(invoice);
      setPaymentIntent(intent);
    } catch (error) {
      console.error('Failed to create payment intent:', error);
    }
  };

  const handlePaymentSuccess = () => {
    setPaymentSuccess(true);
    setPayingInvoice(null);
    setPaymentIntent(null);
    queryClient.invalidateQueries({ queryKey: ['my-invoices'] });
    setTimeout(() => setPaymentSuccess(false), 5000);
  };

  const handlePaymentCancel = () => {
    setPayingInvoice(null);
    setPaymentIntent(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700';
      case 'partial':
        return 'bg-yellow-100 text-yellow-700';
      case 'overdue':
        return 'bg-red-100 text-red-700';
      case 'sent':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-4 w-4" />;
      case 'partial':
      case 'sent':
        return <Clock className="h-4 w-4" />;
      case 'overdue':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const isStripeReady = stripeConfig?.stripeConfigured && isStripeEnabled();
  const unpaidInvoices = invoices?.filter((inv) => ['sent', 'partial', 'overdue'].includes(inv.status)) || [];
  const paidInvoices = invoices?.filter((inv) => inv.status === 'paid') || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Billing & Invoices</h1>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  // Payment modal
  if (payingInvoice && paymentIntent) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Pay Invoice</h1>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b">
            <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Invoice {payingInvoice.invoiceNumber}</h3>
              <p className="text-sm text-gray-500">
                Amount due: {formatCurrency(payingInvoice.balance)}
              </p>
            </div>
          </div>

          <PaymentForm
            clientSecret={paymentIntent.clientSecret}
            amount={payingInvoice.balance}
            type="payment"
            onSuccess={handlePaymentSuccess}
            onCancel={handlePaymentCancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing & Invoices</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and pay your invoices online.
          </p>
        </div>
      </div>

      {/* Payment Success Message */}
      {paymentSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <p className="text-green-800">Payment successful! Your invoice has been updated.</p>
        </div>
      )}

      {/* Outstanding Balance Summary */}
      {unpaidInvoices.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Outstanding</p>
              <p className="text-3xl font-bold text-gray-900">
                {formatCurrency(unpaidInvoices.reduce((sum, inv) => sum + inv.balance, 0))}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">{unpaidInvoices.length} unpaid invoice{unpaidInvoices.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>
      )}

      {/* Unpaid Invoices */}
      {unpaidInvoices.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Outstanding Invoices</h2>

          <div className="space-y-3">
            {unpaidInvoices.map((invoice) => (
              <div
                key={invoice.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
                        <p className="text-sm text-gray-500">
                          {invoice.description || invoice.type} &middot; Due {formatDate(invoice.dueDate)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-medium text-gray-900">{formatCurrency(invoice.balance)}</p>
                        {invoice.amountPaid > 0 && (
                          <p className="text-xs text-gray-500">
                            {formatCurrency(invoice.amountPaid)} paid
                          </p>
                        )}
                      </div>

                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        {invoice.status}
                      </span>

                      {isStripeReady && invoice.balance > 0 && (
                        <button
                          onClick={() => handlePayInvoice(invoice)}
                          disabled={createPaymentIntentMutation.isPending}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                        >
                          Pay Now
                        </button>
                      )}

                      <button
                        onClick={() => setExpandedInvoice(expandedInvoice === invoice.id ? null : invoice.id)}
                        className="p-2 text-gray-400 hover:text-gray-600"
                      >
                        {expandedInvoice === invoice.id ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {expandedInvoice === invoice.id && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Line Items</h4>
                    <div className="space-y-2">
                      {invoice.lineItems.map((item) => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            {item.description} {item.quantity > 1 && `(x${item.quantity})`}
                          </span>
                          <span className="text-gray-900">{formatCurrency(item.total)}</span>
                        </div>
                      ))}
                      {invoice.tax > 0 && (
                        <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                          <span className="text-gray-600">Tax</span>
                          <span className="text-gray-900">{formatCurrency(invoice.tax)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-medium pt-2 border-t border-gray-200">
                        <span>Total</span>
                        <span>{formatCurrency(invoice.total)}</span>
                      </div>
                    </div>

                    {invoice.payments.length > 0 && (
                      <>
                        <h4 className="text-sm font-medium text-gray-700 mt-4 mb-3">Payment History</h4>
                        <div className="space-y-2">
                          {invoice.payments.map((payment) => (
                            <div key={payment.id} className="flex justify-between text-sm">
                              <span className="text-gray-600">
                                {formatDate(payment.processedAt)} ({payment.method})
                              </span>
                              <span className="text-green-600">-{formatCurrency(payment.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Paid Invoices */}
      {paidInvoices.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Payment History</h2>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y">
            {paidInvoices.map((invoice) => (
              <div key={invoice.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
                    <p className="text-sm text-gray-500">
                      {invoice.description || invoice.type} &middot; Paid {invoice.paidDate && formatDate(invoice.paidDate)}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <p className="font-medium text-gray-900">{formatCurrency(invoice.total)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Invoices */}
      {invoices?.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FileText className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">No Invoices</h3>
          <p className="text-gray-500 mt-1">You don't have any invoices yet.</p>
        </div>
      )}

      {!isStripeReady && unpaidInvoices.length > 0 && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-800 font-medium">Online payments not available</p>
            <p className="text-yellow-700 text-sm mt-1">
              Please contact the office to arrange payment.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
